import { type IntranetQuickAccessItem } from '@gb-ticket/shared-types';
import { useState } from 'react';
import { Link } from 'react-router';

import { cn } from '@/lib/utils';
import { IntranetShowMore } from '@/products/intranet/components/intranet-show-more';
import { previewItems } from '@/products/intranet/features/intranet/preview-list';
import {
  isInternalShortcut,
  ShortcutGlyph,
} from '@/products/intranet/features/intranet/shortcut-icons';

/** Ink tokens that flip in dark mode. Brand navy itself stays dark and fails WCAG on the tile. */
const TONE_CLASS = {
  navy: 'text-primary',
  red: 'text-status-danger-foreground',
  green: 'text-success',
  amber: 'text-warning',
} as const;

/** Three-column icon grid. Modules and shortcuts share one Mehr/Weniger list. */
export function IntranetShortcutGrid({
  items: source,
}: {
  items: readonly IntranetQuickAccessItem[];
}) {
  const [expanded, setExpanded] = useState(false);
  const { items, collapsed, canCollapse } = previewItems(source, expanded);
  if (source.length === 0) {
    return null;
  }
  return (
    <div className="space-y-2">
      <ul className="grid grid-cols-3 gap-2">
        {items.map((item) => (
          <li key={`${item.type}-${item.id}`} className="min-w-0">
            <Shortcut item={item} />
          </li>
        ))}
      </ul>
      {collapsed ? (
        <IntranetShowMore label="Mehr anzeigen" onClick={() => setExpanded(true)} />
      ) : null}
      {canCollapse ? (
        <IntranetShowMore label="Weniger anzeigen" onClick={() => setExpanded(false)} />
      ) : null}
    </div>
  );
}

function Shortcut({ item }: { item: IntranetQuickAccessItem }) {
  const className = cn(
    'flex h-[5.25rem] w-full flex-col items-center justify-center gap-1 overflow-hidden rounded-lg border border-border bg-background px-2 py-2 text-center text-xs font-medium hover:bg-muted focus-visible:ring-2 focus-visible:ring-brand-navy focus-visible:outline-none',
    TONE_CLASS[item.tone],
  );
  const label = (
    <span className="line-clamp-2 w-full leading-tight break-words hyphens-auto">{item.label}</span>
  );
  const icon = <ShortcutGlyph name={item.iconName} />;
  if (isInternalShortcut(item.url)) {
    return (
      <Link to={item.url} className={className}>
        {icon}
        {label}
      </Link>
    );
  }
  return (
    <a href={item.url} target="_blank" rel="noopener noreferrer" className={className}>
      {icon}
      {label}
    </a>
  );
}
